'use client';

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { VideoLatencyEstimate } from '@/lib/video-latency';
import { useDroneVideoStream } from './useDroneVideoStream';
import { VideoStreamBody, playsPicture } from './VideoStreamBody';
import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import { VideoLatencyBadge } from './VideoLatencyBadge';
import { CameraControlOverlay, type CameraControlInputs } from './CameraControlOverlay';
import { chooseStream } from './stageCamera';
import { useStageInsets } from './useStageInsets';
import type { ManualAuthorityState } from '@/hooks/useManualControl';
import type { CopVideoStream } from './types';

/**
 * The picture, while somebody is flying by hand.
 *
 * The same stream registry, the same player and the same honest refusals the
 * FEED segment uses — this adds exactly two things, and only these two:
 *
 * 1. **The latency badge**, permanently, because a picture that is seconds old
 *    is a trap for an operator with a hand on the aircraft. See
 *    `VideoLatencyBadge`.
 * 2. **A placement**, driven by the stage layout: a corner inset on MAP, a
 *    column on SPLIT, the stage itself on VIDEO. Same component in all three,
 *    so the badge cannot be present in one layout and missing in another.
 *
 * It never decodes a stream that is already playing in a popped-out window.
 * That rule is the panel's too (`DroneWorkspace.StreamHealth`): two players on
 * one feed is double the bandwidth and CPU for the same picture, on a link that
 * is often the aircraft's.
 */

export interface ManualVideoStageProps {
  droneId: string;
  callsign: string;
  /** Aircraft ground speed, m/s — the badge turns the delay into metres with it. */
  groundSpeedMs?: number | null;
  /** Authority state, so the badge can follow manoeuvre → stop → look. */
  manualState?: ManualAuthorityState | null;
  /** Cameras already playing in their own window; never decoded twice. */
  poppedCameras?: string[];
  /** Corner inset (MAP layout) vs a full box (SPLIT and VIDEO). */
  compact?: boolean;
  /** Camera control over this picture, the same inputs FEED and the popped-out
   *  window take. Absent = picture only. The aircraft's own flight gestures are
   *  on the MAP (`ManualMapControl`), never on this box, so a drag here is
   *  unambiguously a gimbal slew and cannot be read as a flight input. */
  cameraControl?: CameraControlInputs;
  /** Bring every popped-out camera back here. Offered only when all of them are
   *  in their own windows, which is the one state this box has nothing to draw
   *  and the operator's way back would otherwise run through FEED. */
  onDockBack?: () => void;
}

export function ManualVideoStage({
  droneId,
  callsign,
  groundSpeedMs = null,
  manualState = null,
  poppedCameras = [],
  compact = false,
  cameraControl,
  onDockBack,
}: ManualVideoStageProps) {
  const feed = useDroneVideoStream(droneId);
  const [estimate, setEstimate] = useState<VideoLatencyEstimate | null>(null);
  /**
   * WHICH stream the player gave up on, not merely that it did.
   *
   * `playsPicture` answers "could a browser paint this URL", which is true of
   * every HLS stream including one the player has already failed on and covered
   * with "STREAM OFFLINE" — so on its own it stamped
   * "VIDEO LATENCY NOT MEASURED — SITUATIONAL ONLY" across a black tile. The
   * badge's own rule already refuses that for the RTMP branch; this is the same
   * rule, applied to the branch that fails at runtime rather than at registry
   * time.
   *
   * Held as the failing stream's id rather than a boolean because a boolean
   * needs clearing when the camera changes, and the only place to clear it is
   * an effect — which React runs AFTER the child's, so the player's mount-time
   * report was wiped by the parent's reset on the very first render. An id
   * simply stops matching. Null default, and only an explicit 'error' ever sets
   * it, so the `.mp4` branch — which reports nothing — keeps its badge.
   */
  // The player's OWN last word, and which stream it was about. Null means no
  // player has reported at all - the plain <video> mp4 branch never does - which
  // is different from "not playing" and must not be read as a dead picture.
  const [player, setPlayer] = useState<{ id: string; state: HlsStreamState } | null>(null);

  const chosen = chooseStream(feed.streams, poppedCameras);
  const streamId = chosen.stream?.id ?? null;

  /**
   * How far the overlay's bottom band has to rise to clear the chrome floating
   * over the stage's bottom edge. In SPLIT and VIDEO this pane reaches that
   * edge and keeps the map's full height (`ManualStage`), so the band - the
   * gimbal and zoom keys, the failure lines, the latency badge - is what moves,
   * not the pane. Zero in the classic shell, in any test without a provider,
   * and in the MAP layout, where the pane is a top-right PIP nothing floats over.
   */
  const stageInsets = useStageInsets();
  const bottomLift = compact ? 0 : stageInsets.bottom;

  /**
   * The same, for the stage's RIGHT edge, which this pane owns in SPLIT and
   * VIDEO exactly as it owns the bottom one.
   *
   * `CameraControlOverlay` anchors its chrome stack at `inset-2` of this box:
   * the gimbal cross, the zoom trio, and on a scrolling panel the only way to
   * aim the camera at all. The side panel is `z-20` positioned and this pane is
   * `z-auto` inside it, so without this the keys are painted over, unclickable,
   * and still focusable by keyboard - which is WCAG 2.4.11 as well as a control
   * an operator reaches for while hand-flying off this picture.
   *
   * Zero when `compact`, which is the MAP layout's corner PIP: it reaches no
   * edge of the stage, so no chrome anchored to one can be over it.
   */
  const rightLift = compact ? 0 : stageInsets.right;

  /**
   * And the LEFT edge, where the navigation rail floats.
   *
   * In the VIDEO layout `ManualStage` lifts the map out as an absolute minimap,
   * so this pane is `flex-1` from the stage's left edge - which puts the rail
   * over the pane header's callsign and over `CameraControlOverlay`'s readout - the
   * first row of its `inset-2` stack - the gimbal pitch/yaw and zoom ratio an operator is
   * hand-flying off. Under `prefers-reduced-transparency` the rail is opaque, so
   * they are not dimmed, they are gone.
   */
  const leftLift = compact ? 0 : stageInsets.left;

  /**
   * And the TOP edge, which `CopTopStack` began covering in #505 slice 7.
   *
   * The fourth edge, and the one this file was missing. In SPLIT and VIDEO the
   * pane is `flex-1` at the stage's full height, so its top edge IS the stage's
   * top edge - and the stack is `z-30` against this pane's `z-auto`. Underneath
   * it are the top rows of `CameraControlOverlay`'s `inset-2` stack: the pose
   * readout (gimbal pitch/yaw and zoom, which that file calls the only evidence
   * a nudge did anything) and the command rail beside it.
   *
   * It needs no manual session to happen: pressing VIDEO puts this pane on
   * screen (`splitVideoActive`), and the dock roster is in the top stack on any
   * COP that has docks. In a SPLIT or VIDEO session it is the roster plus the
   * manual strip.
   */
  const topLift = compact ? 0 : stageInsets.top;

  // A different camera is a different measurement. Dropping the old one is what
  // stops the badge quoting the previous stream's latency under the new one's
  // picture until the first sample of the new player lands.
  useEffect(() => {
    setEstimate(null);
  }, [streamId]);

  // Only this stream's own report counts; a stale one about the previous camera
  // says nothing about this picture.
  const reportedState = player !== null && player.id === streamId ? player.state : null;
  const playbackFailed = reportedState === 'error';
  const showBadge = playsPicture(chosen.stream) && !playbackFailed;

  return (
    <div
      data-testid="manual-video-stage"
      className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-subtle bg-black"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-white/10 bg-card/95 px-2 py-1">
        <span
          className={cn(
            'callsign text-foreground',
            compact ? 'text-[10px]' : 'text-[11px]',
          )}
        >
          {callsign}
        </span>
        <span
          className={cn(
            'truncate uppercase tracking-[0.08em] text-muted-foreground',
            compact ? 'text-[9px]' : 'text-[10px]',
          )}
        >
          {cameraLabel(chosen.stream)}
        </span>
      </div>

      <div className="relative min-h-0 flex-1">
        {chosen.allPopped ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              Playing in its own window
            </p>
            {onDockBack && (
              <button
                type="button"
                onClick={onDockBack}
                className="rounded-md border border-subtle px-2.5 py-1.5 text-[10px] uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              >
                Dock back here
              </button>
            )}
          </div>
        ) : (
          <VideoStreamBody
            callsign={callsign}
            stream={chosen.stream}
            loading={feed.loading}
            error={feed.error}
            onLatencyEstimate={setEstimate}
            // Inline, and deliberately not memoised: it has to close over the
            // CURRENT stream to file a failure against the right one, and
            // `HlsVideo` reports through a ref of its own — so a fresh identity
            // every render never re-attaches its player and costs nothing.
            onStreamState={(state: HlsStreamState) => {
              // No stream id, nothing to file the report against - and a report
              // that cannot be matched to a stream would be read as being about
              // whichever one is on screen next.
              if (streamId !== null) setPlayer({ id: streamId, state });
            }}
          />
        )}

        {/* Camera control and the latency badge share ONE bottom stack, the
            overlay owning the anchor and the badge riding in its footer. Both
            used to be able to claim `bottom-0`, which is a z-index fight the
            badge wins — hiding the readout and the zoom keys behind it. In the
            stack the bar sits above the badge and neither needs to know the
            other's height, which matters because the badge grows a line when it
            can quote the picture lead.

            The three parts are withheld on three DIFFERENT conditions, and they
            are not interchangeable:

            - The whole stack goes when every camera is popped out — the picture
              is in its own window and so is its control — and when there is no
              stream ROW at all. An aircraft with no camera registered was
              otherwise given three stacked negative sentences about its camera
              in a 288 px box: "no stream registered", a dashed pose readout,
              and CAMERA CONTROL UNAVAILABLE. There is nothing to observe or to
              command without a stream.
            - The BADGE needs `showBadge`, a picture actually being delivered.
              "Video is 3 s behind" over the "no stream registered" message, or
              over the player's own "stream offline", is a caveat about nothing.
            - The BAR renders in every remaining state, because the readout is
              observation rather than command and a viewer is entitled to read
              the gimbal pose while the video link is down (F4). Its gesture
              surface is the part that needs a real picture, and gates itself
              on one inside the overlay (F9). */}
        {!chosen.allPopped && chosen.stream !== null && (cameraControl || showBadge) && (
          /* The overlay anchors every band to this box rather than to the
             picture, so a lifted bottom edge lifts the bottom band with it and
             leaves the readout and the keys at the top where they were. Not
             `pointer-events-none`: the gesture surface inside inherits it. */
          <div
            data-testid="manual-video-controls"
            className="absolute inset-0"
            style={{ bottom: bottomLift, right: rightLift, left: leftLift, top: topLift }}
          >
          <CameraControlOverlay
            stream={chosen.stream}
            // GRABBABLE means painting right now — not merely "has not failed".
            // hls.js leaves the last decoded frame on the <video> while it
            // reconnects, and the scrim over it is pointer-events-none, so a
            // not-failed-but-not-playing player would hand the operator a still
            // image that drags a live gimbal. That is the exact hazard F9 exists
            // for, and "not failed" quietly stopped meaning "live" the moment the
            // player learned to retry.
            pictureLive={reportedState === null || reportedState === 'playing'}
            compact={compact}
            {...cameraControl}
            // Control is only offered when the caller supplied the gates. With
            // no `onCameraCommand` the overlay withholds every control anyway,
            // so a badge-only stage renders the readout and its reason — which
            // is the honest answer to "why can I not point this camera".
            footer={
              showBadge ? (
                <VideoLatencyBadge
                  estimate={estimate}
                  manualState={manualState}
                  groundSpeedMs={groundSpeedMs}
                  compact={compact}
                  stacked
                />
              ) : null
            }
          />
          </div>
        )}
      </div>
    </div>
  );
}

function cameraLabel(stream: CopVideoStream | null): string {
  if (!stream) return 'no camera';
  return (stream.label ?? stream.camera_id ?? 'camera').toString();
}

